import { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import ColorPickerEmbed from "./ColorPickerEmbed";
import GroceryPage from "./pages/GroceryPage";
import GroceryAddItemsPage from "./pages/GroceryAddItemsPage";
import GroceryStatsPage from "./pages/GroceryStatsPage";
import BudgetPage from "./pages/BudgetPage";
import * as THREE from "three";
import "./App.css";

const paletteBaseCount = 4;
const paletteMaxCount = 10;
const paletteScrollStep = 80;
const paletteGridSize = 6;
const paletteNearBlackThreshold = 12;
const paletteNearWhiteThreshold = 243;
const paletteSamplePoints = [
  { x: 0.18, y: 0.22 },
  { x: 0.72, y: 0.22 },
  { x: 0.32, y: 0.55 },
  { x: 0.68, y: 0.62 },
  { x: 0.5, y: 0.32 },
  { x: 0.22, y: 0.78 },
  { x: 0.82, y: 0.5 },
  { x: 0.4, y: 0.15 },
  { x: 0.6, y: 0.82 },
  { x: 0.3, y: 0.4 },
];

const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);

const toHex = (value) => value.toString(16).padStart(2, "0");

const isNearBlack = (red, green, blue) =>
  red < paletteNearBlackThreshold &&
  green < paletteNearBlackThreshold &&
  blue < paletteNearBlackThreshold;

const isNearWhite = (red, green, blue) =>
  red > paletteNearWhiteThreshold &&
  green > paletteNearWhiteThreshold &&
  blue > paletteNearWhiteThreshold;

const buildGridPoints = (size) => {
  const points = [];

  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      points.push({ x: (col + 0.5) / size, y: (row + 0.5) / size });
    }
  }

  return points;
};

const buildPaletteSwatches = (image) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });

  if (!context) {
    return [];
  }

  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const candidates = paletteSamplePoints.concat(
    buildGridPoints(paletteGridSize),
  );
  const seen = new Set();
  const swatches = [];

  for (const point of candidates) {
    const sampleX = Math.floor(
      clampValue(point.x * canvas.width, 0, canvas.width - 1),
    );
    const sampleY = Math.floor(
      clampValue(point.y * canvas.height, 0, canvas.height - 1),
    );
    const [red, green, blue] = context.getImageData(
      sampleX,
      sampleY,
      1,
      1,
    ).data;

    if (isNearBlack(red, green, blue) || isNearWhite(red, green, blue)) {
      continue;
    }
    const hex = `#${toHex(red)}${toHex(green)}${toHex(blue)}`;

    if (seen.has(hex)) {
      continue;
    }

    seen.add(hex);
    swatches.push({ ...point, hex });

    if (swatches.length >= paletteMaxCount) {
      break;
    }
  }

  return swatches;
};

function AppContent() {
  // Modal state for commission booking
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [commissionEmail, setCommissionEmail] = useState("");
  const [commissionMessage, setCommissionMessage] = useState("");
  const [commissionSent, setCommissionSent] = useState(false);

  // For TOS before commission modal
  const [pendingCommission, setPendingCommission] = useState(false);

  const handleContactBookCommission = (e) => {
    e.preventDefault();
    setTosAccepted(false);
    setPendingCommission(true);
    setTosOpen(true);
  };

  const handleCommissionModalClose = () => {
    setShowCommissionModal(false);
    setCommissionEmail("");
    setCommissionMessage("");
  };

  const handleCommissionSentModalClose = () => {
    setCommissionSent(false);
  };

  const handleCommissionSend = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent("Book a Commission");
    const body = encodeURIComponent(
      `Sender Email: ${commissionEmail}\n\n${commissionMessage}`,
    );
    window.location.href = `mailto:kyradrawss01@gmail.com?subject=${subject}&body=${body}`;
    handleCommissionModalClose();
    setCommissionSent(true);
  };
  // Ref to track if TOS modal was opened for contact section's Book a commission
  // Remove duplicate handleContactBookCommission for TOS, only keep modal version below
  const [showTop, setShowTop] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [tosOpen, setTosOpen] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [devtoolsOpen, setDevtoolsOpen] = useState(false);
  const [paletteSwatches, setPaletteSwatches] = useState([]);
  const [paletteCount, setPaletteCount] = useState(paletteBaseCount);
  const [paletteOffset, setPaletteOffset] = useState(0);
  const [pickAnims, setPickAnims] = useState([]);
  const [statCount, setStatCount] = useState(0);
  const [trafficState, setTrafficState] = useState({
    status: "loading",
    totalRequests: 0,
    error: "",
  });
  const heroImageRef = useRef(null);
  const heroSceneRef = useRef(null);
  const lastScrollY = useRef(0);
  const scrollAccumulator = useRef(0);
  const paletteLengthRef = useRef(paletteSamplePoints.length);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setShowTop(currentScrollY > 120);

      const delta = currentScrollY - lastScrollY.current;
      if (delta !== 0) {
        scrollAccumulator.current += delta;
        const stepCount = Math.trunc(
          scrollAccumulator.current / paletteScrollStep,
        );

        if (stepCount !== 0) {
          scrollAccumulator.current -= stepCount * paletteScrollStep;
          setPaletteCount((current) =>
            clampValue(current + stepCount, paletteBaseCount, paletteMaxCount),
          );
          setPaletteOffset((current) => {
            const total = paletteLengthRef.current || 1;
            const next = (current + stepCount) % total;
            return next < 0 ? next + total : next;
          });
        }
      }

      lastScrollY.current = currentScrollY;
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleContextMenu = (event) => {
      const target = event.target;
      if (!target) {
        return;
      }

      const hit = target.closest("img, .art-card__media, .hero-image");
      if (hit) {
        event.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu, {
      capture: true,
    });

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu, {
        capture: true,
      });
    };
  }, []);

  useEffect(() => {
    const isTouchDevice =
      window.matchMedia("(pointer: coarse)").matches ||
      navigator.maxTouchPoints > 0;

    if (isTouchDevice) {
      setDevtoolsOpen(false);
      return undefined;
    }

    const timeThreshold = 120;
    const minDesktopWidth = 1024;
    let intervalId;

    const isDesktopWidth = () => window.innerWidth >= minDesktopWidth;

    const runDebuggerProbe = () => {
      const start = performance.now();
      // eslint-disable-next-line no-new-func
      const debugFn = new Function("debugger");
      debugFn();
      return performance.now() - start;
    };

    const detectDevtools = () => {
      if (!isDesktopWidth()) {
        setDevtoolsOpen(false);
        return;
      }

      let timeOpen = false;

      try {
        timeOpen = runDebuggerProbe() > timeThreshold;
      } catch (error) {
        timeOpen = false;
      }

      setDevtoolsOpen(timeOpen);
    };

    detectDevtools();
    window.addEventListener("resize", detectDevtools);
    intervalId = window.setInterval(detectDevtools, 1200);

    return () => {
      window.removeEventListener("resize", detectDevtools);
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("devtools-open", devtoolsOpen);

    return () => {
      document.body.classList.remove("devtools-open");
    };
  }, [devtoolsOpen]);

  useEffect(() => {
    const heroImage = heroImageRef.current;

    if (!heroImage) {
      return undefined;
    }

    const updatePalette = () => {
      const swatches = buildPaletteSwatches(heroImage);
      paletteLengthRef.current = swatches.length || 1;
      setPaletteSwatches(swatches);
    };

    if (heroImage.complete) {
      updatePalette();
    } else {
      heroImage.addEventListener("load", updatePalette);
    }

    return () => {
      heroImage.removeEventListener("load", updatePalette);
    };
  }, []);

  useEffect(() => {
    const container = heroSceneRef.current;

    if (!container) {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const createSakuraTexture = () => {
      const size = 64;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return null;
      }

      ctx.translate(size / 2, size / 2);
      const petalGradient = ctx.createRadialGradient(0, -10, 4, 0, 0, 30);
      petalGradient.addColorStop(0, "#fbe4ea");
      petalGradient.addColorStop(0.6, "#f7c2d2");
      petalGradient.addColorStop(1, "#f2a9bc");

      for (let i = 0; i < 5; i += 1) {
        ctx.rotate((Math.PI * 2) / 5);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(6, -12, 10, -26);
        ctx.quadraticCurveTo(0, -34, -10, -26);
        ctx.quadraticCurveTo(-6, -12, 0, 0);
        ctx.closePath();
        ctx.fillStyle = petalGradient;
        ctx.fill();
        ctx.strokeStyle = "rgba(242, 169, 188, 0.4)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(0, 0, 4, 0, Math.PI * 2);
      ctx.fillStyle = "#f4b7c2";
      ctx.fill();

      const texture = new THREE.CanvasTexture(canvas);
      if ("colorSpace" in texture) {
        texture.colorSpace = THREE.SRGBColorSpace;
      }
      return texture;
    };
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, 1, 0.1, 100);
    camera.position.z = 8;
    const viewBounds = { width: 10, height: 6 };

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    const sakuraTexture = createSakuraTexture();
    const colorPalette = [
      new THREE.Color("#f7c2d2"),
      new THREE.Color("#f2a9bc"),
      new THREE.Color("#fbe4ea"),
      new THREE.Color("#f6cbd8"),
    ];

    const resize = () => {
      const { width, height } = container.getBoundingClientRect();
      const safeWidth = Math.max(width, 1);
      const safeHeight = Math.max(height, 1);
      renderer.setSize(safeWidth, safeHeight, false);
      camera.aspect = safeWidth / safeHeight;
      camera.updateProjectionMatrix();
      const viewHeight =
        2 *
        Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)) *
        camera.position.z;
      viewBounds.height = viewHeight;
      viewBounds.width = viewHeight * camera.aspect;
    };

    resize();

    const distributePoint = (index, count) => {
      const gridSize = Math.ceil(Math.sqrt(count));
      const cellWidth = viewBounds.width / gridSize;
      const cellHeight = viewBounds.height / gridSize;
      const col = index % gridSize;
      const row = Math.floor(index / gridSize);
      const jitterX = (Math.random() - 0.5) * cellWidth * 1.05;
      const jitterY = (Math.random() - 0.5) * cellHeight * 1.05;

      return {
        x: (col + 0.5) * cellWidth - viewBounds.width / 2 + jitterX,
        y: (row + 0.5) * cellHeight - viewBounds.height / 2 + jitterY,
      };
    };

    const createLayer = ({
      count,
      size,
      opacity,
      driftX,
      driftZ,
      speedMin,
      speedMax,
      attractStrength,
      attractRadius,
      zRange,
      zOffset,
    }) => {
      const positions = new Float32Array(count * 3);
      const colors = new Float32Array(count * 3);
      const anchorX = new Float32Array(count);
      const anchorZ = new Float32Array(count);
      const driftPhase = new Float32Array(count);
      const velocityY = new Float32Array(count);

      for (let i = 0; i < count; i += 1) {
        const i3 = i * 3;
        const distributed = distributePoint(i, count);
        anchorX[i] = distributed.x;
        anchorZ[i] = (Math.random() - 0.5) * zRange + zOffset;
        positions[i3] = anchorX[i];
        positions[i3 + 1] = distributed.y;
        positions[i3 + 2] = anchorZ[i];
        driftPhase[i] = Math.random() * Math.PI * 2;
        velocityY[i] = speedMin + Math.random() * (speedMax - speedMin);

        const color = colorPalette[i % colorPalette.length];
        colors[i3] = color.r;
        colors[i3 + 1] = color.g;
        colors[i3 + 2] = color.b;
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(positions, 3),
      );
      geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

      const material = new THREE.PointsMaterial({
        size,
        map: sakuraTexture || null,
        alphaTest: 0.2,
        depthWrite: false,
        vertexColors: true,
        transparent: true,
        opacity,
      });
      const points = new THREE.Points(geometry, material);
      scene.add(points);

      return {
        geometry,
        material,
        points,
        positions,
        anchorX,
        anchorZ,
        driftPhase,
        velocityY,
        driftX,
        driftZ,
        attractStrength,
        attractRadius,
        zRange,
        zOffset,
      };
    };

    const layers = [
      createLayer({
        count: prefersReducedMotion ? 80 : 220,
        size: 0.16,
        opacity: 0.72,
        driftX: 0.35,
        driftZ: 0.25,
        speedMin: 0.006,
        speedMax: 0.014,
        attractStrength: 0.01,
        attractRadius: 1.6,
        zRange: 6,
        zOffset: 0,
      }),
      createLayer({
        count: prefersReducedMotion ? 24 : 50,
        size: 0.28,
        opacity: 0.9,
        driftX: 0.5,
        driftZ: 0.32,
        speedMin: 0.004,
        speedMax: 0.01,
        attractStrength: 0.02,
        attractRadius: 2.2,
        zRange: 3,
        zOffset: 1.2,
      }),
    ];

    const mouse = { x: 0, y: 0, active: false };
    const handlePointerMove = (event) => {
      const rect = container.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      mouse.x = (x - 0.5) * viewBounds.width;
      mouse.y = (0.5 - y) * viewBounds.height;
      mouse.active = true;
    };
    const handlePointerLeave = () => {
      mouse.active = false;
    };

    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerleave", handlePointerLeave);

    let frame = 0;
    const clock = new THREE.Clock();
    const renderScene = () => {
      const elapsed = clock.getElapsedTime();
      const delta = Math.min(clock.getDelta(), 0.05);

      layers.forEach((layer) => {
        const count = layer.velocityY.length;
        const halfHeight = viewBounds.height / 2;

        for (let i = 0; i < count; i += 1) {
          const i3 = i * 3;
          layer.positions[i3 + 1] -= layer.velocityY[i] * delta * 60;

          if (layer.positions[i3 + 1] < -halfHeight - 1) {
            layer.positions[i3 + 1] = halfHeight + 1 + Math.random();
          }

          const baseX =
            layer.anchorX[i] +
            Math.sin(elapsed * 0.7 + layer.driftPhase[i]) * layer.driftX;
          const baseZ =
            layer.anchorZ[i] +
            Math.cos(elapsed * 0.5 + layer.driftPhase[i]) * layer.driftZ;
          let finalX = baseX;
          let finalY = layer.positions[i3 + 1];

          if (mouse.active && layer.attractStrength > 0) {
            const dx = mouse.x - baseX;
            const dy = mouse.y - finalY;
            const distance = Math.hypot(dx, dy);
            const radius = layer.attractRadius || 1.6;

            if (distance < radius) {
              const influence = (1 - distance / radius) * layer.attractStrength;
              finalX += dx * influence;
              finalY += dy * influence;
            }
          }

          layer.positions[i3] = finalX;
          layer.positions[i3 + 1] = finalY;
          layer.positions[i3 + 2] = baseZ;
        }

        layer.geometry.attributes.position.needsUpdate = true;
      });
      renderer.render(scene, camera);
      frame = window.requestAnimationFrame(renderScene);
    };

    if (!prefersReducedMotion) {
      renderScene();
    } else {
      renderer.render(scene, camera);
    }

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerleave", handlePointerLeave);
      window.cancelAnimationFrame(frame);
      layers.forEach((layer) => {
        scene.remove(layer.points);
        layer.geometry.dispose();
        layer.material.dispose();
      });
      if (sakuraTexture) {
        sakuraTexture.dispose();
      }
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      setStatCount(40);
      return;
    }

    let frame = 0;
    let startTime = 0;
    const duration = 1100;
    const target = 40;

    const tick = (timestamp) => {
      if (!startTime) {
        startTime = timestamp;
      }

      const progress = Math.min((timestamp - startTime) / duration, 1);
      setStatCount(Math.round(progress * target));

      if (progress < 1) {
        frame = window.requestAnimationFrame(tick);
      }
    };

    frame = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const targets = Array.from(document.querySelectorAll(".reveal"));

    if (targets.length === 0) {
      return undefined;
    }

    if (!("IntersectionObserver" in window)) {
      targets.forEach((target) => target.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" },
    );

    targets.forEach((target) => observer.observe(target));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const visibleCount = Math.min(paletteCount, paletteSwatches.length);

    if (visibleCount === 0) {
      setPickAnims([]);
      return;
    }

    setPickAnims(
      Array.from({ length: visibleCount }, () => ({
        duration: 0.7 + Math.random() * 0.7,
        delay: Math.random() * 0.2,
        size: [22, 28, 36][Math.floor(Math.random() * 3)],
      })),
    );
  }, [paletteOffset, paletteCount, paletteSwatches.length]);

  useEffect(() => {
    if (!tosOpen) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setTosOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [tosOpen]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadTraffic = async () => {
      try {
        setTrafficState((prev) => ({ ...prev, status: "loading", error: "" }));
        const response = await fetch("/api/traffic", {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Traffic data unavailable.");
        }

        const payload = await response.json();

        if (!isMounted) {
          return;
        }

        setTrafficState({
          status: "ready",
          totalRequests: payload.totalRequests ?? 0,
          error: "",
        });
      } catch (error) {
        if (!isMounted || error.name === "AbortError") {
          return;
        }

        setTrafficState((prev) => ({
          ...prev,
          status: "error",
          error: error.message || "Traffic data unavailable.",
        }));
      }
    };

    loadTraffic();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const handleBackToTop = (event) => {
    event.preventDefault();
    const topNode = document.getElementById("top");

    if (topNode) {
      topNode.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleOpenTos = () => {
    setNavOpen(false);
    setTosAccepted(false);
    setTosOpen(true);
  };

  const handleProceedToRates = () => {
    if (!tosAccepted) {
      return;
    }
    setTosOpen(false);
    if (pendingCommission) {
      setPendingCommission(false);
      setShowCommissionModal(true);
      return;
    }
    const ratesNode = document.getElementById("rates");
    if (ratesNode) {
      ratesNode.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleImageContextMenu = (event) => {
    event.preventDefault();
  };

  const orderedSwatches =
    paletteSwatches.length === 0
      ? []
      : paletteSwatches.map(
          (_, index) =>
            paletteSwatches[(index + paletteOffset) % paletteSwatches.length],
        );
  const visibleSwatches = orderedSwatches.slice(0, paletteCount);

  const sampleCommissions = [
    "/assets/comms/art1.JPEG",
    "/assets/comms/art2.JPEG",
    "/assets/comms/art3.PNG",
    "/assets/comms/art4.PNG",
    "/assets/comms/art5.PNG",
    "/assets/comms/art6.PNG",
    "/assets/comms/art7.PNG",
    "/assets/comms/art8.JPEG",
    "/assets/comms/art9.PNG",
  ];

  const formatNumber = (value) =>
    new Intl.NumberFormat("en-US").format(value || 0);

  return (
    <div className="page">
      <span id="top" className="top-anchor" aria-hidden="true" />
      <div className="page-3d" ref={heroSceneRef} aria-hidden="true" />
      <div className="ambient" aria-hidden="true">
        <span className="orb orb--one" />
        <span className="orb orb--two" />
        <span className="orb orb--three" />
        <span className="orb orb--four" />
      </div>

      <header className="hero">
        <nav className={`nav${navOpen ? " is-open" : ""}`}>
          <a className="logo" href="#top">
            Kydrawss
          </a>
          <button
            className="nav-toggle"
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={navOpen}
            aria-controls="primary-nav"
            onClick={() => setNavOpen((open) => !open)}
          >
            <span className="nav-toggle__bar" />
          </button>
          <div
            id="primary-nav"
            className={`nav-links${navOpen ? " is-open" : ""}`}
          >
            <a href="#gallery" onClick={() => setNavOpen(false)}>
              Works
            </a>
            <a href="#order" onClick={() => setNavOpen(false)}>
              How to order
            </a>
            <a href="#process" onClick={() => setNavOpen(false)}>
              Process
            </a>
            <a href="#contact" onClick={() => setNavOpen(false)}>
              Contact
            </a>
            <a href="/color-picker" onClick={() => setNavOpen(false)}>
              Color Picker
            </a>
          </div>
          <button className="nav-button" type="button" onClick={handleOpenTos}>
            Book a commission
          </button>
        </nav>

        <div className="hero-visual hero-visual--top">
          <div className="glass-card glass-card--main">
            <div className="glass-top">
              <span>Featured commission</span>
            </div>
            <div className="hero-image" onContextMenu={handleImageContextMenu}>
              <img
                ref={heroImageRef}
                src="/assets/image04.png"
                alt="Luminous Tides main artwork"
                onContextMenu={handleImageContextMenu}
              />
              <div className="hero-image__overlay" aria-hidden="true">
                <span className="stroke stroke--one" />
                <span className="stroke stroke--two" />
                <span className="stroke stroke--three" />
              </div>
              <div className="hero-image__picks" aria-hidden="true">
                {visibleSwatches.map((swatch, index) => (
                  <span
                    key={`hero-pick-${index}`}
                    className="hero-image__pick"
                    style={{
                      left: `${swatch.x * 100}%`,
                      top: `${swatch.y * 100}%`,
                      backgroundColor: swatch.hex,
                      "--pick-travel-duration": `${
                        pickAnims[index]?.duration ?? 0.9
                      }s`,
                      "--pick-travel-delay": `${pickAnims[index]?.delay ?? 0}s`,
                      "--pick-size": `${pickAnims[index]?.size ?? 26}px`,
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="glass-bottom">
              <h3>Welcome to my artwork</h3>
            </div>
          </div>
          <div className="glass-card glass-card--side">
            <p className="chip">Commission: OPEN</p>
            <h4>Slots Remaining: 1</h4>
            <div className="traffic-card">
              <div className="traffic-card__header">
                <span className="traffic-card__label">Visits (30d)</span>
                <span
                  className={`traffic-card__status traffic-card__status--${trafficState.status}`}
                >
                  {trafficState.status === "loading"
                    ? "Loading"
                    : trafficState.status === "error"
                      ? "Offline"
                      : `${formatNumber(trafficState.totalRequests)} visits`}
                </span>
              </div>
              {trafficState.status === "error" ? (
                <p className="traffic-card__note">{trafficState.error}</p>
              ) : (
                <p className="traffic-card__note">Total visits shown above.</p>
              )}
            </div>
          </div>
          <div className="palette-card">
            <p className="chip">Palette</p>
            <div className="palette-swatches" aria-live="polite">
              {visibleSwatches.length === 0 ? (
                <span className="palette-note">Sampling colors...</span>
              ) : (
                visibleSwatches.map((swatch, index) => (
                  <div
                    className="palette-swatch"
                    key={`${swatch.hex}-${index}`}
                  >
                    <span
                      className="palette-swatch__dot"
                      style={{ backgroundColor: swatch.hex }}
                    />
                    <span className="palette-swatch__label">{swatch.hex}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="floating-tag">Since 2022</div>
        </div>

        <div className="hero-grid">
          <div className="hero-text">
            <p className="eyebrow">Commissioned art</p>
            <h1 className="hero-title">Where Your OC Becomes a Masterpiece</h1>
            <div className="hero-stats">
              <div>
                <span className="stat-value">{statCount}+</span>
                <span className="stat-label">Commissions delivered</span>
              </div>
            </div>
            <p className="hero-subtitle">
              High-quality digital illustrations & character art.
            </p>
          </div>
        </div>
      </header>

      <section className="marquee" aria-hidden="true">
        <div className="marquee-track">
          <span>
            OC portraits • Anime-inspired commissions • Soft painterly tones •
            Character design • Detailed outfits •
          </span>
          <span>
            OC portraits • Anime-inspired commissions • Soft painterly tones •
            Character design • Detailed outfits •
          </span>
        </div>
      </section>

      <main>
        <section id="gallery" className="section gallery">
          <div className="section-head reveal">
            <div>
              <p className="eyebrow">Sample commissions</p>
            </div>
          </div>
          <div className="grid gallery-grid">
            {sampleCommissions.map((src, index) => (
              <article className="art-card art-card--media reveal" key={src}>
                <div
                  className="art-card__media"
                  onContextMenu={handleImageContextMenu}
                >
                  <img
                    src={src}
                    alt={`Sample commission ${index + 1}`}
                    loading="lazy"
                    onContextMenu={handleImageContextMenu}
                  />
                </div>
              </article>
            ))}
          </div>
        </section>
        <section id="rates" className="section rates">
          <div className="section-head reveal">
            <div>
              <p className="eyebrow">Ky - Commission rates</p>
              <h2>Pricing guide for commissions</h2>
              <p className="section-subtitle">
                Hello! Thank you for your interest in commissioning me. Please
                review the pricing guide, add-ons, and terms below.
              </p>
            </div>
          </div>
          <div className="rates-grid">
            <div className="rates-card reveal">
              <h3>Basic pricing</h3>
              <div className="rates-table">
                <div className="rates-row rates-head">
                  <span>Type</span>
                  <span>Price (USD)</span>
                  <span>Price (PHP)</span>
                </div>
                <div className="rates-row">
                  <span>Bust (head & shoulders)</span>
                  <span>$64</span>
                  <span>PHP 3,500</span>
                </div>
                <div className="rates-row">
                  <span>Half-body</span>
                  <span>$84</span>
                  <span>PHP 4,500</span>
                </div>
                <div className="rates-row">
                  <span>Thigh-up</span>
                  <span>$94</span>
                  <span>PHP 5,300</span>
                </div>
                <div className="rates-row">
                  <span>Full-body</span>
                  <span>$104</span>
                  <span>PHP 6,000</span>
                </div>
              </div>
            </div>
            <div className="rates-card reveal">
              <h3>Add-ons / extras</h3>
              <div className="rates-table">
                <div className="rates-row rates-head">
                  <span>Add-on</span>
                  <span>Price (USD)</span>
                  <span>Price (PHP)</span>
                </div>
                <div className="rates-row">
                  <span>Detailed outfit & accessories/pets</span>
                  <span>+$9 - $37</span>
                  <span>+PHP 500 - 2,000</span>
                </div>
                <div className="rates-row">
                  <span>Simple background</span>
                  <span>+$9 - $19</span>
                  <span>+PHP 500 - 1,000</span>
                </div>
                <div className="rates-row">
                  <span>Detailed background (room, scenery)</span>
                  <span>+$19 - $55</span>
                  <span>+PHP 1,000 - 3,000</span>
                </div>
                <div className="rates-row">
                  <span>Extra character</span>
                  <span>+100% of base price</span>
                  <span>+100%</span>
                </div>
                <div className="rates-row">
                  <span>Commercial rights (merch, monetized use)</span>
                  <span>+50% - 100% of total</span>
                  <span>+50% - 100%</span>
                </div>
              </div>
            </div>
          </div>
          <div className="rates-grid">
            <div className="rates-card reveal">
              <h3>Terms at a glance</h3>
              <ul className="rates-list">
                <li>Payment: PayPal (USD), GCash/Maya (PHP)</li>
                <li>1-2 minor revisions per stage are free</li>
                <li>Turnaround: 1-6 weeks depending on complexity</li>
                <li>
                  Personal use only unless commercial rights are purchased
                </li>
                <li>Artist retains rights to post work (privacy on request)</li>
                <li>No refund once I started the sketch</li>
              </ul>
            </div>
            <div className="rates-card reveal">
              <h3>Do's & don'ts</h3>
              <div className="rates-duo">
                <div>
                  <p className="rates-label">Will do</p>
                  <ul className="rates-list">
                    <li>Fanart (anime, cartoon, real people)</li>
                    <li>OC</li>
                    <li>Couple and ships (any gender)</li>
                    <li>Slight NSFW</li>
                  </ul>
                </div>
                <div>
                  <p className="rates-label">Will not do</p>
                  <ul className="rates-list">
                    <li>Mecha</li>
                    <li>Old people</li>
                    <li>Hate art</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="order" className="section split">
          <div className="split-text reveal">
            <p className="eyebrow">How to order</p>
            <h2>Commission workflow + rates at a glance.</h2>
            <p className="section-subtitle">
              Rates start with bust, half-body, thigh-up, and full-body. Add-ons
              include outfits, backgrounds, extra characters, and commercial
              rights. Payment via PayPal (USD) or GCash/Maya (PHP). Personal use
              only unless commercial rights are purchased. 1-2 minor revisions
              per stage, turnaround 1-6 weeks, and no refund once the sketch
              starts.
            </p>
            <div className="split-actions">
              <a className="button ghost" href="#contact">
                Start an inquiry
              </a>
            </div>
          </div>
          <div className="split-visual">
            <div className="stack">
              <div className="stack-card stack-card--top">
                <p>Step 01</p>
                <h3>Inquiry</h3>
              </div>
              <div className="stack-card stack-card--mid">
                <p>Step 02</p>
                <h3>Agreement</h3>
              </div>
              <div className="stack-card stack-card--bottom">
                <p>Step 03</p>
                <h3>Payment</h3>
              </div>
              <div className="stack-card">
                <p>Step 04</p>
                <h3>Production</h3>
              </div>
            </div>
          </div>
        </section>

        <section id="process" className="section process">
          <div className="section-head reveal">
            <div>
              <p className="eyebrow">Commission process</p>
              <h2>From inquiry to delivery in four steps.</h2>
            </div>
          </div>
          <div className="grid process-grid">
            <div className="process-card reveal">
              <span className="process-step">01</span>
              <h3>Inquiry</h3>
              <p>
                DM me on Instagram or Twitter (@ky_drawss). Share details of
                your character(s), references, and the type of commission you
                want before I accept.
              </p>
            </div>
            <div className="process-card reveal">
              <span className="process-step">02</span>
              <h3>Agreement</h3>
              <p>
                I will agree to give you a slot and send an invoice (PayPal) or
                payment form (GCash).
              </p>
            </div>
            <div className="process-card reveal">
              <span className="process-step">03</span>
              <h3>Payment</h3>
              <p>
                Full upfront or 50% down payment before the initial sketch. I
                will send the final piece once full payment is received. PayPal
                (USD) or GCash (PHP). No refund once I start the initial sketch.
              </p>
            </div>
            <div className="process-card reveal">
              <span className="process-step">04</span>
              <h3>Production & delivery</h3>
              <p>
                I start sketching after payment and share progress updates. I
                will send a sketch within one week. You have up to 3 chances to
                edit the sketch. I will send the final piece within 1-6 weeks
                via email.
              </p>
            </div>
          </div>
        </section>

        <section id="contact" className="section contact">
          <div className="contact-card reveal">
            <div>
              <p className="eyebrow">Contact</p>
              <h2>Ready to commission a soft, painterly OC portrait?</h2>
              <p className="section-subtitle">
                Share your timeline, format, and budget range. I respond within
                72 hours with a tailored concept outline.
              </p>
            </div>
            <div className="contact-actions">
              <a
                className="button primary"
                href="mailto:kyradrawss01@gmail.com?subject=Commission%20Request"
              >
                hello@kydrawss
              </a>
              <button
                className="button ghost"
                type="button"
                onClick={handleContactBookCommission}
              >
                Book a commission
              </button>
              {showCommissionModal && (
                <div
                  className="modal-overlay"
                  role="presentation"
                  onClick={handleCommissionModalClose}
                >
                  <div
                    className="modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="commission-modal-title"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <h2 id="commission-modal-title">Book a Commission</h2>
                    <form onSubmit={handleCommissionSend}>
                      <label htmlFor="commission-email">Your Email</label>
                      <input
                        id="commission-email"
                        type="email"
                        required
                        value={commissionEmail}
                        onChange={(e) => setCommissionEmail(e.target.value)}
                        placeholder="your@email.com"
                        autoComplete="email"
                      />
                      <label htmlFor="commission-message">Message</label>
                      <textarea
                        id="commission-message"
                        required
                        value={commissionMessage}
                        onChange={(e) => setCommissionMessage(e.target.value)}
                        placeholder="Describe your commission request..."
                        rows={5}
                      />
                      <div className="modal-actions">
                        <button
                          className="button ghost"
                          type="button"
                          onClick={handleCommissionModalClose}
                        >
                          Cancel
                        </button>
                        <button className="button primary" type="submit">
                          Send Email
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {commissionSent && (
                <div
                  className="modal-overlay"
                  role="presentation"
                  onClick={handleCommissionSentModalClose}
                >
                  <div
                    className="modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="commission-sent-title"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <h2 id="commission-sent-title">Thank you!</h2>
                    <p>
                      Your commission request has been prepared in your email
                      app. I will respond within 72 hours. If you don't see a
                      reply, please check your spam folder or contact me on
                      Instagram/X.
                    </p>
                    <div className="modal-actions">
                      <button
                        className="button primary"
                        type="button"
                        onClick={handleCommissionSentModalClose}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <a
                className="button ghost"
                href="https://www.instagram.com/ky_drawss"
              >
                Instagram
              </a>
              <a className="button ghost" href="https://www.x.com/ky_drawss">
                X (Twitter)
              </a>
            </div>
          </div>
        </section>
      </main>

      {tosOpen && (
        <div
          className="modal-overlay"
          role="presentation"
          onClick={() => setTosOpen(false)}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tos-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="tos-title">Ky drawss — terms of service</h2>
            <p>
              If you want to hire me for a commission, please read through this
              page to get an overview of my services. Before hiring me, please
              make sure to have read my Terms of Service.
            </p>
            <ul className="tos-list">
              <li>
                <strong>General:</strong> I accept commissions for personal use
                only unless commercial rights are purchased. Please provide
                clear references and concept ideas before I start. I reserve the
                right to decline any request I am uncomfortable with or does not
                suit my style.
              </li>
              <li>
                <strong>Payment:</strong> Payment is in USD via PayPal or PHP
                via GCash or Maya. Commissions are paid upfront or via 50/50
                split (half before sketch, half before final delivery). No rush
                deadline.
              </li>
              <li>
                <strong>Process & revisions:</strong> You will receive updates
                (sketch, base color, etc.) throughout the process. 1-2 minor
                revisions are allowed per stage. Major changes after
                finalization may incur extra charges. Turnaround time depends on
                the artwork complexity (usually 1-6 weeks per piece).
              </li>
              <li>
                <strong>Client rights:</strong> You may post the artwork with
                proper credit (@ky_drawss). You may use it for icons, headers,
                or printing for personal use. You may not edit, trace, or alter
                the artwork. You may not reproduce, redistribute, or use it for
                profit (merch, prints, etc.) without commercial rights.
              </li>
              <li>
                <strong>Artist rights:</strong> I retain full rights to the
                artwork, including posting it on my portfolio, website, or
                social media, and using it for promotional materials or
                examples. If you would like your commission to remain private,
                please request this in advance (privacy fee may apply).
              </li>
              <li>
                <strong>Refund & cancellation:</strong> No refunds once I have
                started the sketch. If I need to cancel due to unforeseen
                circumstances, a full refund will be issued.
              </li>
            </ul>
            <p>By commissioning me, you agree to these terms. Thank you!</p>
            <label className="tos-check">
              <input
                type="checkbox"
                checked={tosAccepted}
                onChange={(event) => setTosAccepted(event.target.checked)}
              />
              I agree to the commission terms.
            </label>
            <div className="modal-actions">
              <button
                className="button ghost"
                type="button"
                onClick={() => setTosOpen(false)}
              >
                Cancel
              </button>
              <button
                className="button primary"
                type="button"
                onClick={handleProceedToRates}
                disabled={!tosAccepted}
              >
                Accept & Continue to Rates
              </button>
            </div>
          </div>
        </div>
      )}

      <a
        className={`back-to-top${showTop ? "" : " is-hidden"}`}
        href="#top"
        aria-label="Back to top"
        onClick={handleBackToTop}
      >
        ↑
      </a>

      <footer className="footer reveal">
        <div>
          <p className="logo">Kydrawss</p>
          <p className="muted">Digital art direction · PH</p>
        </div>
        <div className="footer-links">
          <a href="#gallery">Gallery</a>
          <a href="#order">How to order</a>
          <a href="#process">Process</a>
          <a href="#contact">Contact</a>
        </div>
        <div>
          <p className="muted">Developer: drlshx</p>
          <a
            className="footer-github"
            href="https://github.com/drlshx"
            target="_blank"
            rel="noreferrer"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M12 2C6.48 2 2 6.58 2 12.26c0 4.5 2.87 8.31 6.84 9.66.5.1.68-.22.68-.48 0-.24-.01-1.04-.02-1.88-2.78.62-3.37-1.22-3.37-1.22-.46-1.19-1.12-1.5-1.12-1.5-.9-.63.07-.62.07-.62 1 .07 1.53 1.06 1.53 1.06.9 1.57 2.36 1.12 2.94.86.09-.67.35-1.12.64-1.38-2.22-.26-4.56-1.13-4.56-5.04 0-1.11.38-2.01 1.02-2.72-.1-.26-.45-1.3.1-2.71 0 0 .83-.27 2.72 1.04.8-.23 1.64-.34 2.48-.34.84 0 1.69.12 2.48.34 1.89-1.31 2.72-1.04 2.72-1.04.55 1.41.2 2.45.1 2.71.64.71 1.02 1.61 1.02 2.72 0 3.92-2.34 4.78-4.57 5.03.36.32.69.95.69 1.92 0 1.39-.01 2.51-.01 2.85 0 .26.18.59.69.49 3.96-1.36 6.82-5.16 6.82-9.66C22 6.58 17.52 2 12 2z"
              />
            </svg>
            github.com/drlshx
          </a>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/color-picker" element={<ColorPickerEmbed />} />
        <Route path="/grocery" element={<GroceryPage />} />
        <Route path="/grocery/add-items" element={<GroceryAddItemsPage />} />
        <Route path="/grocery/stats" element={<GroceryStatsPage />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="*" element={<AppContent />} />
      </Routes>
    </Router>
  );
}

export default App;
